import { FAILURE, POST, PUT, SUCCESS } from "../utils/const/http-const";
import { getHeaders } from "./utils";

export const MENSAJE_ACCION_KANBAN_MAX_LENGTH = 1024;

const BASE_URL =
    "https://gestiondeflota.boltrack.net/apiultimamillav3/api/workflow-programacion-solicitud-producto";

const parseJsonField = (value) => {
    if (value && typeof value === "object") return value;
    if (typeof value === "string" && value.trim()) {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch (err) {
            return {};
        }
    }
    return {};
};

const parseNotificarA = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim()) {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (err) {
            return [];
        }
    }
    return [];
};

const parseMoverAKanban = (value) => {
    if (!value || typeof value !== "object" || value.value == null) return null;
    return {
        value: value.value,
        label: value.label ?? String(value.value),
    };
};

const createAccionConfigItemDefault = (accion = {}) => ({
    idAccion: accion.id ?? accion.idAccion ?? null,
    descripcion: accion.descripcion ?? "",
    color: accion.color ?? null,
    mensaje: "",
    notificarA: [],
    moverAKanban: null,
});

const normalizarAccionConfigItem = (value, accionMeta = {}) => {
    const raw = value && typeof value === "object" ? value : {};
    return {
        idAccion: raw.idAccion ?? accionMeta.id ?? null,
        descripcion: raw.descripcion ?? accionMeta.descripcion ?? "",
        color: raw.color ?? accionMeta.color ?? null,
        mensaje: String(raw.mensaje || "").slice(
            0,
            MENSAJE_ACCION_KANBAN_MAX_LENGTH
        ),
        notificarA: parseNotificarA(raw.notificarA),
        moverAKanban: parseMoverAKanban(raw.moverAKanban ?? raw.estadoViaje),
    };
};

export function crearConfigProgramacionKanbanDefault({
    idKanban = null,
    acciones = [],
} = {}) {
    return {
        id: null,
        idKanban,
        acciones: (Array.isArray(acciones) ? acciones : [])
            .filter((accion) => accion?.habilitado !== 0)
            .map((accion) => createAccionConfigItemDefault(accion)),
    };
}

export function normalizarConfigProgramacionKanban(value, accionesMeta = []) {
    const config = value && typeof value === "object" ? value : {};
    const metaById = {};
    (Array.isArray(accionesMeta) ? accionesMeta : []).forEach((accion) => {
        if (accion?.id != null) metaById[accion.id] = accion;
    });

    let accionesRaw = [];
    if (Array.isArray(config.acciones)) {
        accionesRaw = config.acciones;
    } else if (config.acciones && typeof config.acciones === "object") {
        accionesRaw = Object.keys(config.acciones).map((idAccion) => ({
            idAccion,
            ...parseJsonField(config.acciones[idAccion]),
        }));
    }

    const accionesNormalizadas = {};
    accionesRaw.forEach((item) => {
        const normalized = normalizarAccionConfigItem(
            item,
            metaById[item?.idAccion] ?? {}
        );
        if (normalized.idAccion != null) {
            accionesNormalizadas[normalized.idAccion] = normalized;
        }
    });

    (Array.isArray(accionesMeta) ? accionesMeta : [])
        .filter((accion) => accion?.habilitado !== 0)
        .forEach((accion) => {
            const current = accionesNormalizadas[accion.id];
            accionesNormalizadas[accion.id] = current
                ? {
                    ...current,
                    descripcion: current.descripcion || accion.descripcion || "",
                    color: current.color || accion.color || null,
                }
                : createAccionConfigItemDefault(accion);
        });

    return {
        id: config.id ?? null,
        idKanban: config.idKanban ?? null,
        acciones: Object.values(accionesNormalizadas),
    };
}

export function toPayloadConfigProgramacionKanban(config, accionesMeta = []) {
    const normalized = normalizarConfigProgramacionKanban(config, accionesMeta);
    return {
        id: normalized.id,
        idKanban: normalized.idKanban,
        acciones: normalized.acciones.map((accion) => ({
            idAccion: accion.idAccion,
            descripcion: accion.descripcion,
            color: accion.color,
            mensaje: accion.mensaje,
            notificarA: accion.notificarA,
            moverAKanban: accion.moverAKanban,
        })),
    };
}

export async function obtenerListaWorkflowProgramacionSolicitudProducto(obj = {}) {
    try {
        const { code, body, message } = await fetch(
            `${BASE_URL}/obtener-lista-workflow-programacion-solicitud-producto`,
            {
                headers: getHeaders(),
                method: POST,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());

        if (code === SUCCESS && Array.isArray(body)) {
            return {
                error: false,
                data: body.map((row) =>
                    normalizarConfigProgramacionKanban({
                        ...row,
                        acciones: Array.isArray(row.acciones)
                            ? row.acciones
                            : parseJsonField(row.acciones),
                    })
                ),
            };
        }
        return { error: true, message, data: [] };
    } catch (err) {
        console.log("obtenerListaWorkflowProgramacionSolicitudProducto err");
        console.log(err);
        return { error: true, message: err, data: [] };
    }
}

export async function crearWorkflowProgramacionSolicitudProducto(
    config,
    accionesMeta = []
) {
    try {
        const payload = toPayloadConfigProgramacionKanban(config, accionesMeta);
        const { code, body, message } = await fetch(
            `${BASE_URL}/crear-workflow-programacion-solicitud-producto`,
            {
                headers: getHeaders(),
                method: POST,
                body: JSON.stringify(payload),
            }
        ).then((res) => res.json());

        if (code === SUCCESS) {
            const merged =
                body && typeof body === "object"
                    ? { ...payload, ...body }
                    : {
                        ...payload,
                        id: typeof body === "string" ? body : payload.id,
                    };
            return {
                error: false,
                data: normalizarConfigProgramacionKanban(merged, accionesMeta),
            };
        }
        if (code === FAILURE) return { error: true, message };
        return { error: true, message };
    } catch (err) {
        console.log("crearWorkflowProgramacionSolicitudProducto err");
        console.log(err);
        return { error: true, message: err };
    }
}

export async function notificarAccionKanban({
    idKanban,
    idAccion,
    idTargetKanban,
    item,
}) {
    try {
        const { code, body, message } = await fetch(
            `${BASE_URL}/notificar-accion-kanban`,
            {
                headers: getHeaders(),
                method: POST,
                body: JSON.stringify({
                    idKanban,
                    idAccion,
                    idTargetKanban,
                    item,
                }),
            }
        ).then((res) => res.json());

        if (code === SUCCESS) {
            return { error: false, total: body?.total ?? null };
        }
        return { error: true, message };
    } catch (err) {
        console.log("notificarAccionKanban err");
        console.log(err);
        return { error: true, message: err };
    }
}

export async function actualizarWorkflowProgramacionSolicitudProducto(
    config,
    accionesMeta = []
) {
    try {
        const payload = toPayloadConfigProgramacionKanban(config, accionesMeta);
        const { code, body, message } = await fetch(
            `${BASE_URL}/actualizar-workflow-programacion-solicitud-producto`,
            {
                headers: getHeaders(),
                method: PUT,
                body: JSON.stringify(payload),
            }
        ).then((res) => res.json());

        if (code === SUCCESS) {
            const merged =
                body && typeof body === "object"
                    ? { ...payload, ...body }
                    : payload;
            return {
                error: false,
                data: normalizarConfigProgramacionKanban(merged, accionesMeta),
            };
        }
        if (code === FAILURE) return { error: true, message };
        return { error: true, message };
    } catch (err) {
        console.log("actualizarWorkflowProgramacionSolicitudProducto err");
        console.log(err);
        return { error: true, message: err };
    }
}